import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import minorMemberService from '../../services/minorMemberService';
import { addNotification } from '../../store/slices/uiSlice';
import { formatDate } from '../../utils/date';
import './MinorMemberDetail.css';

const Field = ({ label, value }) => (
  <div className="detail-field">
    <span className="detail-label">{label}</span>
    <span className="detail-value">{value || '—'}</span>
  </div>
);

const BoolField = ({ label, value }) => (
  <div className="detail-field">
    <span className="detail-label">{label}</span>
    <span className={`detail-value ${value ? 'text-success' : 'text-secondary'}`}>
      {value ? '是' : '否'}
    </span>
  </div>
);

// ── Family Graph ──────────────────────────────────────────────────────────────
const PARENT_RELATIONS    = new Set(['父親', '母親', '繼父', '繼母', '養父', '養母', '監護人', '父', '母']);
const UNCLE_AUNT_RELATIONS = new Set(['伯父', '伯母', '叔父', '叔母', '舅父', '舅母', '姑父', '姑母', '姨父', '姨母']);
const SIBLING_RELATIONS   = new Set(['兄', '姊', '弟', '妹', '堂兄', '堂弟', '堂姊', '堂妹', '表兄', '表弟', '表姊', '表妹']);
const GRANDPARENT_RELATIONS = new Set(['祖父', '祖母', '外祖父', '外祖母', '爺爺', '嫲嫲', '外公', '外婆']);
const CHILD_RELATIONS     = new Set(['兒子', '女兒', '孩子', '子女']);

const MEMBER_TYPE_LABEL = { adult: '成人', children: '兒童', teenage: '少年' };
const MEMBER_TYPE_COLOR = { adult: '#059669', children: '#0ea5e9', teenage: '#7c3aed' };

const FamilyNode = ({ person }) => {
  const typeLabel = person.member_type ? MEMBER_TYPE_LABEL[person.member_type] : null;
  const typeColor = person.member_type ? MEMBER_TYPE_COLOR[person.member_type] : '#6b7280';
  return (
    <div className="family-node" style={{ borderColor: person.is_church_member ? '#16a34a' : '#d1d5db' }}>
      <div className="family-node-relation">{person.relation}</div>
      <div className="family-node-name">{person.chi_name || '—'}</div>
      {person.is_church_member && (
        <>
          <div className="family-node-worship">{person.worship_code || '教會會員'}</div>
          {typeLabel && (
            <div style={{ fontSize: '10px', color: '#fff', background: typeColor,
              borderRadius: '3px', padding: '1px 5px', marginTop: '3px', display: 'inline-block' }}>
              {typeLabel}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const FamilyGraph = ({ member }) => {
  const family = member.family_members || [];
  const typeColor = member.member_type === 'children' ? '#0ea5e9' : '#7c3aed';

  if (family.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '64px 24px', color: '#9ca3af' }}>
        <i className="fas fa-users" style={{ fontSize: '3rem', marginBottom: '16px', display: 'block', opacity: 0.3 }}></i>
        <div style={{ fontSize: '15px' }}>暫無家庭成員資料</div>
        <div style={{ fontSize: '13px', marginTop: '6px' }}>請在編輯頁面的「家庭狀況」分頁新增成員</div>
      </div>
    );
  }

  const grandparents   = family.filter(f => GRANDPARENT_RELATIONS.has(f.relation));
  const parents        = family.filter(f => PARENT_RELATIONS.has(f.relation));
  const unclesAunts    = family.filter(f => UNCLE_AUNT_RELATIONS.has(f.relation));
  const olderSiblings  = family.filter(f => f.relation === '兄' || f.relation === '姊' || f.relation === '堂兄' || f.relation === '堂姊' || f.relation === '表兄' || f.relation === '表姊');
  const youngerSiblings = family.filter(f => f.relation === '弟' || f.relation === '妹' || f.relation === '堂弟' || f.relation === '堂妹' || f.relation === '表弟' || f.relation === '表妹');
  const children       = family.filter(f => CHILD_RELATIONS.has(f.relation));
  const others         = family.filter(f =>
    !GRANDPARENT_RELATIONS.has(f.relation) &&
    !PARENT_RELATIONS.has(f.relation) &&
    !UNCLE_AUNT_RELATIONS.has(f.relation) &&
    !SIBLING_RELATIONS.has(f.relation) &&
    !CHILD_RELATIONS.has(f.relation)
  );

  const hasAbove = grandparents.length > 0 || parents.length > 0 || unclesAunts.length > 0;

  return (
    <div className="family-tree">

      {/* Grandparents */}
      {grandparents.length > 0 && (
        <div className="family-level">
          <div className="family-level-label">祖父母 / 外祖父母</div>
          <div className="family-level-nodes">
            {grandparents.map((p, i) => <FamilyNode key={i} person={p} />)}
          </div>
        </div>
      )}
      {grandparents.length > 0 && parents.length > 0 && <div className="family-connector-v" />}

      {/* Parents */}
      {parents.length > 0 && (
        <div className="family-level">
          <div className="family-level-label">父母</div>
          <div className="family-level-nodes">
            {parents.map((p, i) => <FamilyNode key={i} person={p} />)}
          </div>
        </div>
      )}

      {/* Uncles / Aunts */}
      {unclesAunts.length > 0 && (
        <div className="family-level">
          <div className="family-level-label">伯叔姑舅姨</div>
          <div className="family-level-nodes">
            {unclesAunts.map((p, i) => <FamilyNode key={i} person={p} />)}
          </div>
        </div>
      )}

      {hasAbove && <div className="family-connector-v" />}

      {/* Self row (always shown) with siblings flanking */}
      <div className="family-level">
        <div className="family-level-label">{olderSiblings.length + youngerSiblings.length > 0 ? '兄弟姊妹' : ''}</div>
        <div className="family-level-nodes">
          {olderSiblings.map((p, i) => <FamilyNode key={`elder-${i}`} person={p} />)}

          {/* SELF node */}
          <div className="family-node family-node-self" style={{ borderColor: typeColor, background: `${typeColor}15` }}>
            <div className="family-node-relation" style={{ background: typeColor, color: '#fff', borderRadius: '4px' }}>本人</div>
            <div className="family-node-worship-id" style={{ color: typeColor }}>{member.worship_id || '—'}</div>
            <div className="family-node-name" style={{ fontWeight: 700 }}>{member.chi_name}</div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
              {member.member_type === 'children' ? '兒童' : '少年'}
            </div>
          </div>

          {youngerSiblings.map((p, i) => <FamilyNode key={`younger-${i}`} person={p} />)}
        </div>
      </div>

      {/* Children */}
      {children.length > 0 && (
        <>
          <div className="family-connector-v" />
          <div className="family-level">
            <div className="family-level-label">子女</div>
            <div className="family-level-nodes">
              {children.map((p, i) => <FamilyNode key={i} person={p} />)}
            </div>
          </div>
        </>
      )}

      {/* Others */}
      {others.length > 0 && (
        <div className="family-others">
          <div className="family-level-label">其他</div>
          <div className="family-level-nodes">
            {others.map((p, i) => <FamilyNode key={i} person={p} />)}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="family-legend">
        <span className="family-legend-item family-legend-church">■ 本教會會員</span>
        <span className="family-legend-item family-legend-other">■ 非教會會員</span>
      </div>
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const MinorMemberDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('detail');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => { loadMember(); }, [id]);

  const loadMember = async () => {
    setLoading(true);
    try {
      const data = await minorMemberService.getById(id);
      setMember(data);
    } catch (err) {
      dispatch(addNotification({ type: 'error', title: '錯誤', message: '無法載入會員資料' }));
      navigate('/minor-members');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText.toLowerCase() !== 'delete') return;
    try {
      await minorMemberService.delete(id);
      dispatch(addNotification({ type: 'success', title: '成功', message: '會員已刪除' }));
      navigate('/minor-members');
    } catch (err) {
      dispatch(addNotification({ type: 'error', title: '錯誤', message: '刪除失敗' }));
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner-border" role="status" />
        <p>載入中...</p>
      </div>
    );
  }

  if (!member) return null;

  const memberTypeLabel = { children: '兒童', teenage: '少年' }[member.member_type] || member.member_type;
  const statusLabel = member.status_name === 'active' ? '在籍' : '停籍';
  const statusClass = member.status_name === 'active' ? 'badge-success' : 'badge-secondary';

  return (
    <div className="minor-member-detail-container">
      {/* Header */}
      <div className="detail-header">
        <div className="detail-title-area">
          <div className="worship-id-large">{member.worship_id}</div>
          <div>
            <h2 className="detail-name">{member.chi_name}</h2>
            {member.eng_name && <p className="detail-eng-name">{member.eng_name}</p>}
          </div>
          <span className={`badge badge-lg ${member.member_type === 'children' ? 'badge-info' : 'badge-purple'}`}>
            {memberTypeLabel}
          </span>
          <span className={`badge badge-lg ${statusClass}`}>{statusLabel}</span>
        </div>
        <div className="detail-actions">
          <Link to={`/minor-members/${id}/edit`} className="btn btn-warning">
            <i className="fas fa-edit"></i> 編輯
          </Link>
          <button className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>
            <i className="fas fa-trash"></i> 刪除
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/minor-members')}>
            <i className="fas fa-arrow-left"></i> 返回
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="detail-tabs">
        <button
          className={`detail-tab-btn${activeTab === 'detail' ? ' active' : ''}`}
          onClick={() => setActiveTab('detail')}
        >
          <i className="fas fa-id-card mr-1"></i> 詳細資料
        </button>
        <button
          className={`detail-tab-btn${activeTab === 'family' ? ' active' : ''}`}
          onClick={() => setActiveTab('family')}
        >
          <i className="fas fa-sitemap mr-1"></i> 家庭關係圖
          {member.family_members && member.family_members.length > 0 && (
            <span className="detail-tab-badge">{member.family_members.length}</span>
          )}
        </button>
      </div>

      {/* Content cards */}
      {activeTab === 'detail' && (
      <div className="detail-grid">

        {/* Personal */}
        <div className="detail-card">
          <div className="detail-card-title"><i className="fas fa-user mr-2"></i>個人資料</div>
          <Field label="中文姓名" value={member.chi_name} />
          <Field label="英文姓名" value={member.eng_name} />
          <Field label="性別" value={member.sex_name} />
          <Field label="出生日期" value={member.date_of_birth ? formatDate(member.date_of_birth) : null} />
          <Field label="就讀級別" value={member.edu_grade} />
          <Field label="就讀學校" value={member.school_name} />
          <Field label="入會日期" value={member.date_of_join ? formatDate(member.date_of_join) : null} />
          <Field label="登記日期" value={member.create_date_time ? formatDate(member.create_date_time) : null} />
        </div>

        {/* Contact */}
        <div className="detail-card">
          <div className="detail-card-title"><i className="fas fa-phone mr-2"></i>聯絡資料</div>
          <Field label="手提電話" value={member.mobile} />
          <Field label="住宅電話" value={member.tel_home} />
          <Field label="傳真" value={member.fax} />
          <Field label="電郵" value={member.email} />
          <div className="detail-divider" />
          <Field label="緊急聯絡人" value={member.emergency_contact_name} />
          <Field label="緊急聯絡電話" value={member.emergency_contact_tel} />
          <Field label="關係" value={member.emergency_contact_relation} />
        </div>

        {/* Address */}
        <div className="detail-card">
          <div className="detail-card-title"><i className="fas fa-home mr-2"></i>地址</div>
          <div className="address-block">
            <div className="address-label">住宅地址</div>
            {member.region || member.street || member.building ? (
              <div>
                {member.region && <div>{member.region}</div>}
                {member.street && <div>{member.street}</div>}
                {member.building && <div>{member.building}</div>}
              </div>
            ) : <span className="text-muted">—</span>}
          </div>
          <div className="address-block mt-2">
            <div className="address-label">通訊地址</div>
            {member.postal_region || member.postal_street || member.postal_building ? (
              <div>
                {member.postal_region && <div>{member.postal_region}</div>}
                {member.postal_street && <div>{member.postal_street}</div>}
                {member.postal_building && <div>{member.postal_building}</div>}
              </div>
            ) : <span className="text-muted">與住宅地址相同</span>}
          </div>
        </div>

        {/* Health */}
        <div className="detail-card">
          <div className="detail-card-title"><i className="fas fa-heartbeat mr-2"></i>健康狀況</div>
          <BoolField label="需要定時服藥" value={member.needs_medication} />
          {member.needs_medication && <Field label="藥物詳情" value={member.medication_reason} />}
          <BoolField label="有過敏症" value={member.has_allergy} />
          {member.has_allergy && <Field label="過敏詳情" value={member.allergy_detail} />}
          <BoolField label="可參加一般體力活動" value={member.can_exercise} />
          {member.health_notes && <Field label="健康備註" value={member.health_notes} />}
        </div>

        {/* Family */}
        {member.family_members && member.family_members.length > 0 && (
          <div className="detail-card family-card">
            <div className="detail-card-title"><i className="fas fa-users mr-2"></i>家庭狀況</div>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>關係</th>
                  <th>姓名</th>
                  <th>本教會</th>
                  <th>敬拜號</th>
                </tr>
              </thead>
              <tbody>
                {member.family_members.map((f, i) => (
                  <tr key={i}>
                    <td>{f.relation}</td>
                    <td>{f.chi_name || '—'}</td>
                    <td>{f.is_church_member ? '✓' : '—'}</td>
                    <td>{f.worship_code || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Ministry & Consent */}
        <div className="detail-card">
          <div className="detail-card-title"><i className="fas fa-church mr-2"></i>事奉 / 同意聲明</div>
          <Field label="區" value={member.districts} />
          <Field label="組" value={member.groups} />
          <div className="detail-divider" />
          <BoolField label="同意拍攝相片" value={member.photo_consent} />
          <BoolField label="同意使用個人資料" value={member.data_use_consent} />
          {member.remark && <Field label="備註" value={member.remark} />}
        </div>

      </div>
      )}

      {/* Family Graph tab */}
      {activeTab === 'family' && (
        <div className="detail-card" style={{ padding: '24px' }}>
          <FamilyGraph member={member} />
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>確認刪除</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="alert alert-danger">
                <strong><i className="fas fa-exclamation-triangle"></i> 此操作不可復原</strong>
              </div>
              <p>你確定要刪除 <strong>{member.chi_name}</strong> (敬拜號: {member.worship_id}) 的記錄嗎？</p>
              <p className="text-muted">請輸入 <code>delete</code> 確認。</p>
              <input
                type="text"
                className="form-control"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="輸入 delete"
                autoFocus
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>取消</button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleteConfirmText.toLowerCase() !== 'delete'}
              >
                <i className="fas fa-trash"></i> 確認刪除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MinorMemberDetail;
